import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { usersApi, UserResponse } from '../lib/users';
import { useAuth } from '../hooks/useAuth';
import UsersList from '../components/users/UsersList';
import { UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { canManageUsers } from '../utils/role-utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type NoticeState = {
  message: string;
  variant?: 'default' | 'destructive';
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [notice, setNotice] = useState<NoticeState | null>(null);

  useEffect(() => {
    const state = location.state as { notice?: NoticeState } | null;
    if (state?.notice?.message) {
      setNotice(state.notice);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const hasPermission = canManageUsers(currentUser?.role);
  const isSuperUser = currentUser?.role === 'super_user';
  const isSiteAdmin = currentUser?.role === 'site_admin';

  // Get page title based on role
  const getPageTitle = () => {
    if (isSuperUser) return 'Users';
    if (isSiteAdmin) return 'End Users';
    return 'Users';
  };

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
    enabled: hasPermission, // Only fetch if user has permission
  });

  // Site admins only see end users in their organisation (when they have one).
  const filteredUsers = isSiteAdmin
    ? users?.filter((user) => {
        if (user.role !== 'end_user') return false;
        if (!currentUser?.organisationId) return true;
        return user.organisationId === currentUser.organisationId;
      })
    : users;


  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const handleCreate = () => {
    navigate('/users/new');
  };

  const handleEdit = (user: UserResponse) => {
    navigate(`/users/${user.id}`);
  };

  const handleDelete = async (id: string) => {
    setUserToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };


  // Only super_user and site_admin can access this page
  if (!hasPermission) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>You do not have permission to access this page.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="px-2 py-3 sm:px-0">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold font-heading">{getPageTitle()}</h1>
        <Button onClick={handleCreate} className="self-start sm:self-auto">
          <UserPlus className="h-4 w-4 mr-2" />
          {isSiteAdmin ? 'Add End User' : 'Add User'}
        </Button>
      </div>

      {notice && (
        <Alert
          variant={notice.variant === 'destructive' ? 'destructive' : 'default'}
          className="mb-4"
        >
          <AlertDescription>{notice.message}</AlertDescription>
        </Alert>
      )}

      {isSiteAdmin && (
        <Alert className="mb-4">
          <AlertDescription>
            {currentUser?.organisationId
              ? 'You can manage end users in your organisation. You can assign them to groups within your sites.'
              : 'You can manage end users for your assigned sites. You can assign them to groups within your sites.'}
          </AlertDescription>
        </Alert>
      )}

      <UsersList
        users={filteredUsers || []}
        onEdit={handleEdit}
        onDelete={handleDelete}
        currentUserId={currentUser?.id}
        isDeleting={deleteMutation.isPending}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

